import React, { useState, useEffect } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import Select from "react-select";

function AssignConcessionPlan() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId = localStorage.getItem("centerId");

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      concessionTypeId: null,
      centerId: null, // filter dropdown
    },
  });

  const [concessionPlans, setConcessionPlans] = useState([]);
  const [allCenters, setAllCenters] = useState([]); // For filter dropdown
  const [centersData, setCentersData] = useState([]); // From API with status
  const [selectedCenters, setSelectedCenters] = useState([]); // User selection
  const [loading, setLoading] = useState(false);

  const concessionTypeId = watch("concessionTypeId");
  const selectedCenterFilter = watch("centerId");

  // Fetch Concession Plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await get(
          `ConcessionType/GetAllConcessionTypeByOrganizationIdAsync?organizationId=${organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setConcessionPlans(response || []);
      } catch (error) {
        toast.error("Failed to load concession plans");
      }
    };
    if (organizationId && token) fetchPlans();
  }, [organizationId, token]);

  // Fetch All Centers (for filter dropdown)
  useEffect(() => {
    const fetchAllCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const centers = response || [];
        setAllCenters(centers);

        if (
          savedCenterId &&
          savedCenterId !== "null" &&
          savedCenterId !== "0"
        ) {
          const saved = centers.find((c) => c.id === parseInt(savedCenterId));
          if (saved) {
            setValue("centerId", { value: saved.id, label: saved.name });
          }
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };
    if (organizationId && token) fetchAllCenters();
  }, [organizationId, token, savedCenterId, setValue]);

  // Fetch Centers with Assignment Status
  useEffect(() => {
    const fetchCenters = async () => {
      if (!concessionTypeId?.value) {
        setCentersData([]);
        setSelectedCenters([]);
        return;
      }

      setLoading(true);
      try {
        const centerIdFilter = selectedCenterFilter?.value || 0;

        const response = await get(
          `ConcessionType/GetCenterByOrganizationAndLevelsId?organizationId=${organizationId}&centerId=${centerIdFilter}&concessionTypeId=${concessionTypeId.value}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const centers = response.center || [];

        setCentersData(centers);

        // Pre-check centers where status === true
        const preChecked = centers
          .filter((center) => center.status === true)
          .map((center) => center.centerId);

        setSelectedCenters(preChecked);
      } catch (error) {
        console.error("Error loading centers:", error);
        toast.error("Failed to load centers");
        setCentersData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCenters();
  }, [concessionTypeId, selectedCenterFilter, organizationId, token]);

  // Toggle checkbox
  const toggleCenter = (centerId) => {
    setSelectedCenters((prev) =>
      prev.includes(centerId)
        ? prev.filter((id) => id !== centerId)
        : [...prev, centerId]
    );
  };

  // Send full sync: all centers with correct status 1 or 0
  const transformToPayload = () => {
    return centersData.map((center) => ({
      centerId: center.centerId,
      centerClassId: 0, // not needed
      concessionTypeId: concessionTypeId.value,
      status: selectedCenters.includes(center.centerId) ? 1 : 0,
      createdBy: localStorage.getItem("userId") || 1,
    }));
  };

  const onSubmit = async () => {
    // if (selectedCenters.length === 0) {
    //   toast.error("Please select at least one center");
    //   return;
    // }

    const payload = transformToPayload();

    // console.log("Sending payload:", payload); // Debug
    // return;

    try {
      const response = await post(
        "ConcessionType/AssignconcessionTypeCenterClassesAsync",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.isSuccess) {
        toast.success("Success!");
      } else {
        toast.error(response.message || "Failed to save");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred");
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Assign Concession Plan to Centers</h2>
      </div>

      <form className="new-form-style" onSubmit={handleSubmit(onSubmit)}>
        <div className="row g-3">
          {/* Left: Controls */}
          <div className="col-md-5">
            <div className="card">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Concession Plan{" "}
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name="concessionTypeId"
                      control={control}
                      rules={{ required: "Required" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          isClearable
                          options={concessionPlans.map((p) => ({
                            value: p.id,
                            label: p.name,
                          }))}
                          placeholder="Select Plan"
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                        />
                      )}
                    />
                    {errors.concessionTypeId && (
                      <small className="text-danger">Required</small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Filter by Center (Optional)
                    </label>
                    <Controller
                      name="centerId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          isClearable
                          isDisabled={
                            savedCenterId &&
                            savedCenterId !== "null" &&
                            savedCenterId !== "0"
                          }
                          options={allCenters.map((c) => ({
                            value: c.id,
                            label: c.name,
                          }))}
                          placeholder="All Centers"
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Center Checkboxes */}
          <div className="col-md-7">
            <div className="card">
              <div className="card-body">
                {loading ? (
                  <p>Loading centers...</p>
                ) : centersData.length > 0 ? (
                  <>
                    <h6>Select Centers to Assign</h6>
                    <div
                      className="border rounded p-3"
                      style={{ maxHeight: "520px", overflowY: "auto" }}
                    >
                      {centersData.map((center) => (
                        <div
                          key={center.centerId}
                          className="form-check mb-2 d-flex align-items-center"
                        >
                          <input
                            type="checkbox"
                            className="form-check-input me-3"
                            id={`center-${center.centerId}`}
                            checked={selectedCenters.includes(center.centerId)}
                            onChange={() => toggleCenter(center.centerId)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`center-${center.centerId}`}
                          >
                            {center.centerName}
                            {center.status &&
                              !selectedCenters.includes(center.centerId) && (
                                <span className="badge bg-warning text-dark ms-2">
                                  Will Remove
                                </span>
                              )}
                            {selectedCenters.includes(center.centerId) && (
                              <span className="badge bg-success ms-2">
                                {center.status
                                  ? "Already Assigned"
                                  : "Will Assign"}
                              </span>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>

                    <div className="mt-3">
                      <button
                        type="submit"
                        className="btn btn-primary btn-sm"
                        disabled={isSubmitting 
                          // || selectedCenters.length === 0
                        }
                      >
                        {isSubmitting ? "Saving..." : "Save Assignment"}
                      </button>
                    </div>
                  </>
                ) : concessionTypeId ? (
                  <p className="text-muted">No centers found.</p>
                ) : (
                  <p className="text-muted">
                    Please select a concession plan first.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default AssignConcessionPlan;
